import { type ReactElement, type ReactNode, type Ref, useCallback } from 'react'
import cx from 'classnames'
import { ModalDialog, ModalDialogSearchView } from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import {
  Autocomplete,
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
} from 'react-aria-components'
import styles from './DocumentPickerDialog.module.css'
import { AutoSkeleton, useIsSkeleton } from '@/components/Skeleton'
import { Spinner } from '@/components/Spinner'
import { mediaQueryMatchStream } from '@/utils/browser-util'
import { useObservableValue } from '@/hooks/rx-hooks'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import { DocumentThumbnail } from '@/features/docs/components/DocumentThumbnail'
import { useComposedRefs } from '@radix-ui/react-compose-refs'

export interface DocumentPickerDialogProps {
  isOpen: boolean
  onClose?: () => unknown
  searchText: string
  searchPlaceholder?: string
  onSearchTextChange: (searchText: string) => unknown
  children?: ReactNode
  'aria-label'?: string
  renderEmptyState?: (searchText: string) => ReactNode
  testId?: string
  doneButtonTestId?: string
}

/**
 * Dialog for picking documents.
 */
export function DocumentPickerDialog({
  children,
  isOpen,
  onClose,
  searchText,
  searchPlaceholder = 'Search for documents',
  onSearchTextChange,
  'aria-label': ariaLabel = 'Choose documents',
  renderEmptyState,
  testId,
  doneButtonTestId,
}: DocumentPickerDialogProps) {
  const renderEmpty = useCallback(() => {
    if (renderEmptyState) {
      return renderEmptyState(searchText)
    }

    return (
      <DocumentPickerEmptyState
        title={searchText ? `No matches` : 'No documents'}
        description={
          searchText
            ? `No document matched '${searchText}'`
            : 'Eligible documents will appear here.'
        }
      />
    )
  }, [searchText, renderEmptyState])

  return (
    <ModalDialog
      size="lg"
      sheetSizing="stretch"
      isOpen={isOpen}
      onClose={onClose}
      aria-label={ariaLabel}
      testId={testId}
    >
      <Autocomplete disableAutoFocusFirst>
        <ModalDialogSearchView
          value={searchText}
          onChange={onSearchTextChange}
          placeholder={searchPlaceholder}
          action={
            <Button
              variant="text"
              label="Done"
              onPress={onClose}
              testId={doneButtonTestId}
            />
          }
        >
          <AriaMenu
            aria-label="Documents"
            className={styles.list}
            renderEmptyState={renderEmpty}
          >
            {children}
          </AriaMenu>
        </ModalDialogSearchView>
      </Autocomplete>
    </ModalDialog>
  )
}

interface DocumentPickerItemProps {
  onAction?: () => unknown
  isPending?: boolean
  title: string
  description?: string
  isSkeleton?: boolean
  thumbnail: ReactElement
  ref?: Ref<HTMLDivElement>
  testId?: string
}

const hasRoomForThumbnail$ = mediaQueryMatchStream(
  'screen and (min-width: 390px)',
)

/**
 * Document Picker Item
 */
export function DocumentPickerItem({
  onAction,
  isPending,
  title,
  description,
  isSkeleton,
  thumbnail,
  ref,
  testId,
}: DocumentPickerItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  const { varyPercent, index, ref: skeletonListRef } = useSkeletonListContext()
  const hasRoomForThumbnail = useObservableValue(hasRoomForThumbnail$)
  const mergedRef = useComposedRefs(ref, skeletonListRef)

  return (
    <AriaMenuItem
      onAction={onAction}
      isDisabled={isPending || isSkeleton}
      textValue={title}
      ref={mergedRef}
      data-test={testId}
      className={({ isFocused, isDisabled }) =>
        cx(
          styles.item,
          isFocused && !isDisabled && styles.focused,
          isDisabled && styles.disabled,
          isSkeleton && styles.skeleton,
        )
      }
    >
      {hasRoomForThumbnail && (
        <div className={styles.thumbnail}>{thumbnail}</div>
      )}

      <div className={styles.info}>
        <p className={styles.title}>
          <AutoSkeleton
            isSkeleton={isSkeleton}
            width={varyPercent(index % 3 === 0 ? 50 : 80)}
          >
            {title}
          </AutoSkeleton>
        </p>
        {(description || isSkeleton) && (
          <p className={styles.description}>
            <AutoSkeleton isSkeleton={isSkeleton} width={varyPercent(40)}>
              {description}
            </AutoSkeleton>
          </p>
        )}
      </div>

      {isPending && (
        <div className={styles.pending}>
          <Spinner size={16} />
        </div>
      )}
    </AriaMenuItem>
  )
}

export interface DocumentPickerItemSkeletonProps {
  ref?: Ref<HTMLDivElement>
}

/**
 * Document Picker Item Skeleton
 */
export function DocumentPickerItemSkeleton({
  ref,
}: DocumentPickerItemSkeletonProps) {
  return (
    <DocumentPickerItem
      title="Placeholder"
      description="Placeholder"
      isSkeleton
      thumbnail={<DocumentThumbnail isSkeleton />}
      ref={ref}
    />
  )
}

interface DocumentPickerEmptyStateProps {
  title: ReactNode
  description: ReactNode
}

/**
 * Document Picker Empty State
 */
export function DocumentPickerEmptyState({
  title,
  description,
}: DocumentPickerEmptyStateProps) {
  return (
    <div className={styles.emptyState}>
      <p className={styles.emptyStateTitle}>{title}</p>
      <p className={styles.emptyStateDescription}>{description}</p>
    </div>
  )
}
